import type { TypeUploadOnceResult } from '../types';

import type { TypeAttemptFn, TypeUploadFeature, TypeUploadPart } from './types';

// ----------------------------------------------------------------------------

export type TypeRunGateDeps = {
    isAborted: () => boolean;
    waitForResume?: () => Promise<void>;
};

// ----------------------------------------------------------------------------

/*
 * Builds the attempt chain for one item out of the enabled features.
 *
 * Composition order is innermost first: the earlier a feature sits in the array, the closer it is
 * to the request. That is what decides nesting when several features wrap — with `[retry, chunk]`
 * each chunk is retried on its own; with `[chunk, retry]` a failure would restart the whole file.
 * `createFeatures` is the one place that order is set.
 *
 * The abort/pause gate is not a feature: an aborted or paused run must not open a request whatever
 * is enabled, so it is always the outermost layer. Features that loop (retry) re-check it between
 * their own attempts.
 */
function composeAttempt(base: TypeAttemptFn, plugins: TypeUploadFeature[], deps: TypeRunGateDeps): TypeAttemptFn {
    let attempt = base;

    for (const plugin of plugins) {
        if (plugin.wrap) {
            attempt = plugin.wrap(attempt);
        }
    }

    return async (part?: TypeUploadPart): Promise<TypeUploadOnceResult> => {
        if (deps.isAborted()) {
            return { ok: false, status: 0, reason: 'abort' };
        }

        if (deps.waitForResume) {
            await deps.waitForResume();
        }

        if (deps.isAborted()) {
            return { ok: false, status: 0, reason: 'abort' };
        }

        return attempt(part);
    };
}

// ----------------------------------------------------------------------------

export default composeAttempt;
